import React, { useState } from 'react';
import { format } from 'date-fns';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { cn } from '../lib/utils';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * MonthPicker — selects a YYYY-MM string.
 *
 * Props:
 * - value: "YYYY-MM" or ''
 * - onChange: (yyyymm) => void
 * - testId: optional
 */
export const MonthPicker = ({ value, onChange, testId }) => {
  const [year, setYear] = useState(() => {
    if (value) return parseInt(value.split('-')[0]);
    return new Date().getFullYear();
  });

  const selected = value ? { y: parseInt(value.split('-')[0]), m: parseInt(value.split('-')[1]) } : null;

  const displayLabel = value
    ? format(new Date(parseInt(value.split('-')[0]), parseInt(value.split('-')[1]) - 1, 1), 'MMMM yyyy')
    : 'Select month';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={cn(
            'inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-300 text-sm font-bold text-black hover:border-[#FF0059] transition-colors bg-white min-w-[200px]'
          )}
        >
          <CalendarIcon className="h-4 w-4 text-[#FF0059]" />
          {displayLabel}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-4" align="start">
        <div className="flex items-center justify-between mb-3">
          <button
            type="button"
            onClick={() => setYear((y) => y - 1)}
            className="p-1.5 rounded-md hover:bg-gray-100"
            data-testid="month-picker-prev-year"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="font-black text-base">{year}</div>
          <button
            type="button"
            onClick={() => setYear((y) => y + 1)}
            className="p-1.5 rounded-md hover:bg-gray-100"
            data-testid="month-picker-next-year"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {MONTHS.map((m, idx) => {
            const isSelected = selected && selected.y === year && selected.m === idx + 1;
            return (
              <button
                key={m}
                type="button"
                onClick={() => onChange(`${year}-${String(idx + 1).padStart(2, '0')}`)}
                className={cn(
                  'py-2 rounded-md text-sm font-bold transition-colors',
                  isSelected
                    ? 'bg-[#FF0059] text-white'
                    : 'hover:bg-[#FFE5EE] text-gray-700'
                )}
                data-testid={`month-picker-${idx + 1}`}
              >
                {m}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
